import { useEffect, useState, type FormEvent } from 'react'
import { api, ApiError } from '../lib/api'
import { useTurnstile } from '../lib/turnstile'

/**
 * /signup -- the account, and only the account. The family comes after the
 * email is confirmed: the link in that email signs the person in and lands
 * them on the onboarding steps.
 *
 * `callbackURL` is where the confirmation link should land. An invited
 * partner arrives from /join and goes back there; everyone else goes to
 * /welcome, which is onboarding.
 */
export function SignUp({ callbackURL = '/welcome', lockedEmail }: { callbackURL?: string; lockedEmail?: string }) {
  const [siteKey, setSiteKey] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState(lockedEmail ?? '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const turnstile = useTurnstile(siteKey)

  useEffect(() => {
    api
      .signupConfig()
      .then((c) => setSiteKey(c.turnstileSiteKey))
      .catch(() => setSiteKey(''))
  }, [])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy || turnstile.token === null) return
    setBusy(true)
    setError(null)
    try {
      await api.signUp({
        name: name.trim(),
        email: email.trim(),
        password,
        turnstileToken: turnstile.token,
        callbackURL,
      })
      setSent(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign up. Check your connection and try again.')
      turnstile.reset()
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div className="lock">
        <div className="lock-card">
          <div className="lock-logo" aria-hidden="true">
            📬
          </div>
          <h1>Check your email</h1>
          <p className="sub">One tap to confirm, then we set up your family.</p>
          <p className="muted" style={{ fontSize: 14 }}>
            We sent a link to <b>{email.trim()}</b>. Open it on this device if you can — it signs you
            straight in. Nothing in the inbox? Check spam, or give it a minute.
          </p>
          <p className="muted" style={{ fontSize: 13, marginTop: 14 }}>
            Already had an account? The email says so, and{' '}
            <a href="/">sign in</a> is the way back.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="lock">
      <form className="lock-card" onSubmit={submit}>
        <div className="lock-logo" aria-hidden="true">
          🥪
        </div>
        <h1>{lockedEmail ? 'Create your account' : 'Sign up free'}</h1>
        <p className="sub">
          {lockedEmail
            ? 'A password for the address the invitation went to.'
            : 'Your account first; your family, kid and pantry next.'}
        </p>

        <div className="stack">
          {error && <div className="banner error">{error}</div>}

          <div className="field">
            <label htmlFor="su-name">Your first name</label>
            <input
              id="su-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="given-name"
              maxLength={40}
              autoFocus
              required
            />
          </div>
          <div className="field">
            <label htmlFor="su-email">Email</label>
            <input
              id="su-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              readOnly={Boolean(lockedEmail)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="su-pass">Password</label>
            <input
              id="su-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <span className="hint">At least 8 characters.</span>
          </div>

          {siteKey ? <div ref={turnstile.ref} className="turnstile" /> : null}
          {turnstile.failed && (
            <div className="banner error">
              The “I am human” check could not load. Reload the page, or try another browser.
            </div>
          )}

          <button
            className="btn primary"
            type="submit"
            disabled={busy || !name.trim() || !email || password.length < 8 || turnstile.token === null}
          >
            {busy ? 'One moment…' : 'Create account'}
          </button>

          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Have an account? <a href="/">Sign in</a>
          </p>
        </div>
      </form>
    </div>
  )
}
